import React from "react";
import { StyleSheet, View, useWindowDimensions } from "react-native";
import {
  NormalText,
  PrimaryButton,
  SecondaryButton,
} from "../../../common/native";
import { ADMIN_TOKENS } from "../../../../styles/adminTokens";
import { TDeployWidgetOnboardingStep } from "./deployWidgetOnboardingContent";

type DeployWidgetOnboardingCardProps = {
  step: TDeployWidgetOnboardingStep;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onDismiss: () => void;
};

const DeployWidgetOnboardingCard: React.FC<DeployWidgetOnboardingCardProps> = ({
  step,
  stepIndex,
  totalSteps,
  onNext,
  onDismiss,
}) => {
  const { width, height } = useWindowDimensions();
  const isMobile = Math.min(width, height) < 768;

  return (
    <View
      style={[styles.card, isMobile ? styles.cardMobile : styles.cardDesktop]}
    >
      <View style={styles.header}>
        <NormalText
          text="Deploy setup tour"
          fontWeight="600"
          style={styles.eyebrow}
        />
        <NormalText
          text={`${stepIndex + 1} of ${totalSteps}`}
          style={styles.stepCount}
        />
      </View>
      <NormalText text={step.title} fontWeight="600" style={styles.title} />
      <NormalText text={step.description} style={styles.description} />
      <View style={[styles.actions, isMobile ? styles.actionsMobile : null]}>
        <SecondaryButton
          text="Dismiss"
          onPress={onDismiss}
          fullWidth={isMobile}
          style={styles.secondaryAction}
        />
        <PrimaryButton
          text={step.primaryActionLabel}
          onPress={onNext}
          fullWidth={isMobile}
          style={styles.primaryAction}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: ADMIN_TOKENS.color.surface,
    borderRadius: ADMIN_TOKENS.radius.xxl,
    borderWidth: ADMIN_TOKENS.borderWidth.sm,
    borderColor: ADMIN_TOKENS.color.border,
    padding: ADMIN_TOKENS.spacing.xxl,
    gap: ADMIN_TOKENS.spacing.lg,
    ...ADMIN_TOKENS.shadow.card,
  },
  cardDesktop: {
    width: 360,
  },
  cardMobile: {
    width: "100%",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    gap: ADMIN_TOKENS.spacing.md,
  },
  eyebrow: {
    color: ADMIN_TOKENS.color.accentDark,
    fontSize: ADMIN_TOKENS.fontSize.sm,
    lineHeight: ADMIN_TOKENS.lineHeight.sm,
  },
  stepCount: {
    color: ADMIN_TOKENS.color.textMuted,
    fontSize: ADMIN_TOKENS.fontSize.sm,
    lineHeight: ADMIN_TOKENS.lineHeight.sm,
  },
  title: {
    color: ADMIN_TOKENS.color.textPrimary,
    fontSize: ADMIN_TOKENS.fontSize.xxl,
    lineHeight: ADMIN_TOKENS.lineHeight.xl,
  },
  description: {
    color: ADMIN_TOKENS.color.textSecondary,
    fontSize: ADMIN_TOKENS.fontSize.md,
    lineHeight: ADMIN_TOKENS.lineHeight.md,
  },
  actions: {
    flexDirection: "row",
    alignItems: "center",
    gap: ADMIN_TOKENS.spacing.md,
  },
  actionsMobile: {
    flexDirection: "column-reverse",
  },
  secondaryAction: {
    flex: 1,
    minHeight: 46,
  },
  primaryAction: {
    flex: 1,
    minHeight: 46,
  },
});

export default DeployWidgetOnboardingCard;
